import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Entry } from 'contentful'

import { AVAILABLE_LANGUAGES } from '@/i18n'
import { AVAILABLE_COUNTRIES } from '@/constants/countries'

import { getUserLocaleInfo } from './i18n'
import type { BlogPost } from './contentful'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function createPathFromCategory(path?: string) {
  return path?.toLowerCase().replace(/\s+/g, '-')
}

export function createCategoryFromPath(path?: string) {
  if (!path) return ''

  return path.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
}

export function clearPathFromLangAndCountry(path: string) {
  return path.replace(/\/([a-zA-Z]{2}(?:-[a-zA-Z]{2})?)\b/g, '')
}

export function createPath(url: string, search?: string) {
  const path = new URL(url)

  return ({
    country,
    lang,
    onlyBase,
  }: {
    country?: string
    lang?: string
    onlyBase?: boolean
  }) => {
    const { userLanguage, userCountry } = getUserLocaleInfo()
    const clearedPath = clearPathFromLangAndCountry(path.pathname)

    const preferredLang = lang || userLanguage
    const preferredCountry = country || userCountry

    const langBase = `/${preferredLang}-${preferredCountry}`
    const params = onlyBase ? '' : clearedPath
    const searchParams = search ? search : ''

    return `${langBase}${params}${searchParams}`
  }
}

export function getFirstLettersOfFullName(fullName?: string) {
  if (!fullName) return ''

  const [firstName, lastName] = fullName.split(' ')

  return `${firstName[0]}${lastName[0]}`
}

export function getCategoryName(
  categories: Record<string, string>[],
  category?: string,
) {
  for (const entry of categories) {
    const matchedKey = Object.keys(entry).find((key) => entry[key] === category)
    if (matchedKey) {
      return entry['en-US']
    }
  }
}

export function getRedirectLink(currentUrl: string, langCountryPart: string) {
  const languages = AVAILABLE_LANGUAGES.filter((lang) => !lang.includes('-'))

  const availablePages = AVAILABLE_COUNTRIES.flatMap((country) => {
    return languages.map((lang) => `${lang}-${country.code}`)
  })

  const defaultLocale = `${AVAILABLE_LANGUAGES[0]}-${AVAILABLE_COUNTRIES[0].code}`

  if (
    !availablePages.includes(langCountryPart) &&
    availablePages
      .map((page) => page.toLowerCase())
      .includes(langCountryPart.toLowerCase())
  ) {
    const [lang, country] = langCountryPart.split('-')
    const correctedUrl = currentUrl.replace(
      langCountryPart,
      `${lang}-${country.toUpperCase()}`,
    )

    return correctedUrl
  }

  if (!availablePages.includes(langCountryPart)) {
    const newUrl = currentUrl.replace(langCountryPart, defaultLocale)

    return newUrl
  }

  return null
}

type PostProps = {
  post: Entry<BlogPost, 'WITHOUT_UNRESOLVABLE_LINKS', string>
  lang?: string
}

export function getCTATitle({ post, lang }: PostProps) {
  if (!lang) {
    return post.fields.title
  }

  switch (lang) {
    case 'pt':
      return post.fields.titlePortugueseBrazil ?? post.fields.title
    default:
      return post.fields.title
  }
}

export function getPostTitle({ post, lang }: PostProps) {
  if (!post) return ''

  if (!lang) {
    return post.fields.title
  }

  switch (lang) {
    case 'pt':
      return post.fields.titlePortugueseBrazil ?? post.fields.title
    default:
      return post.fields.title
  }
}

export function getPostPreview({ post, lang }: PostProps) {
  if (!post) return ''

  if (!lang) {
    return post.fields.preview
  }

  switch (lang) {
    case 'pt':
      return post.fields.previewPortugueseBrazil ?? post.fields.preview
    default:
      return post.fields.preview
  }
}

export function getPostIntro({ post, lang }: PostProps) {
  if (!lang) {
    return post.fields.intro
  }

  switch (lang) {
    case 'pt':
      return post.fields.introPortugueseBrazil ?? post.fields.intro
    default:
      return post.fields.intro
  }
}

export function getPostContent({ post, lang }: PostProps) {
  if (!lang) {
    return post.fields.content
  }

  switch (lang) {
    case 'pt':
      return post.fields.contentPortugueseBrazil ?? post.fields.content
    default:
      return post.fields.content
  }
}

export function getCategoryTitle({ post, lang }: PostProps) {
  if (!lang) {
    return post.fields.category?.fields.name
  }

  switch (lang) {
    case 'pt':
      return (
        post?.fields.category?.fields.namePortugueseBrazil ??
        post?.fields.category?.fields.name
      )
    default:
      return post?.fields.category?.fields.name
  }
}
